import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileData } from '@work4all/models/lib/File';

import { SearchResultItem as SearchResultItemType } from '../../hooks/use-global-search-query';
import { SearchResultItem } from '../../input/search-result-item/SearchResultItem';

import { prepareSuggestionEls } from './prepareSuggestionEls';

interface Props {
  searchData: SearchResultItemType[];
  onSelected: (el: FileData, type: Entities) => void;
  pathMapping: Partial<Record<Entities, string>>;
  defaultCustomer?: FileData[];
  defaultProject?: FileData[];
  defaultSupplier?: FileData[];
  showDefaultResults: boolean;
  showTitles?: boolean;
}

export const ResultList: React.FC<Props> = ({
  searchData,
  onSelected,
  pathMapping,
  showTitles = false,
  defaultCustomer = [],
  defaultProject = [],
  defaultSupplier = [],
  showDefaultResults,
}) => {
  const { t } = useTranslation();

  const customerList = useMemo(() => {
    return prepareSuggestionEls({
      items: searchData.filter(
        (x) =>
          x.__typename === 'Kunde' ||
          (x.__typename === 'Ansprechpartner' &&
            x.businessPartner?.data?.__typename === 'Kunde')
      ),
      pathname: pathMapping[Entities.customer],
      elementSelected: (data) => onSelected(data, Entities.customer),
    });
  }, [onSelected, pathMapping, searchData]);

  const supplierList = useMemo(() => {
    return prepareSuggestionEls({
      items: searchData.filter(
        (x) =>
          x.__typename === 'Lieferant' ||
          (x.__typename === 'Ansprechpartner' &&
            x.businessPartner?.data?.__typename === 'Lieferant')
      ),
      pathname: pathMapping[Entities.supplier],
      elementSelected: (data) => onSelected(data, Entities.supplier),
    });
  }, [onSelected, pathMapping, searchData]);

  const projectList = useMemo(() => {
    return searchData
      .filter((x) => x.__typename === 'Projekt')
      .map((item, index) =>
        scopeResultItem(
          {
            id: item.id + '',
            number: item.number,
            name: item.name,
            projectStatus: item.projectStatus,
          },
          pathMapping[Entities.project],
          (data) => onSelected(data, Entities.project),
          index
        )
      );
  }, [onSelected, pathMapping, searchData]);

  const defaultLastUsed = useMemo(() => {
    return {
      [Entities.customer]: defaultCustomer.map((el, index) =>
        scopeResultItem(
          el,
          pathMapping[Entities.customer],
          (data) => onSelected(data, Entities.customer),
          index
        )
      ),
      [Entities.supplier]: defaultSupplier.map((el, index) =>
        scopeResultItem(
          el,
          pathMapping[Entities.supplier],
          (data) => onSelected(data, Entities.supplier),
          index
        )
      ),

      [Entities.project]: defaultProject.map((el, index) =>
        scopeResultItem(
          el,
          pathMapping[Entities.project],
          (data) => onSelected(data, Entities.project),
          index
        )
      ),
    };
  }, []);

  const showCustomersStack =
    (customerList.length > 0 && !showDefaultResults) ||
    (defaultLastUsed[Entities.customer].length > 0 && showDefaultResults);

  const showSuppliersStack =
    (supplierList.length > 0 && !showDefaultResults) ||
    (defaultLastUsed[Entities.supplier].length > 0 && showDefaultResults);

  const showProjectsStack =
    (projectList.length > 0 && !showDefaultResults) ||
    (defaultLastUsed[Entities.project].length > 0 && showDefaultResults);

  return (
    <Stack direction="column" alignItems={'stretch'}>
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {showCustomersStack && (
          <Stack direction="column">
            {showTitles && (
              <Title>
                {t('COMMON.CUSTOMER', { count: customerList.length })}
              </Title>
            )}
            {showDefaultResults
              ? defaultLastUsed[Entities.customer]
              : customerList}
          </Stack>
        )}
        {showSuppliersStack && (
          <Stack direction="column">
            {showTitles && (
              <Title>
                {t('COMMON.SUPPLIER', { count: supplierList.length })}
              </Title>
            )}
            {showDefaultResults
              ? defaultLastUsed[Entities.supplier]
              : supplierList}
          </Stack>
        )}
        {showProjectsStack && (
          <Stack direction="column">
            {showTitles && (
              <Title>
                {t('COMMON.PROJECT', { count: projectList.length })}
              </Title>
            )}
            {showDefaultResults
              ? defaultLastUsed[Entities.project]
              : projectList}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

const scopeResultItem = (
  item: FileData,
  path: string,
  onSelected: (data: FileData) => void,
  index: number
) => {
  return (
    <SearchResultItem
      key={`${item.id}-${index}`}
      id={item.id?.toString()}
      number={item.number?.toString()}
      href={`${path}/${item.id}`}
      title={item.name || ''}
      extinct={item.extinct}
      projectStatus={item.projectStatus}
      onClick={() =>
        onSelected({
          id: item.id + '',
          number: item.number,
          name: item.name,
        })
      }
    />
  );
};

const Title: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const theme = useTheme();
  return (
    <Stack
      direction={'row'}
      alignItems="center"
      gap=".25rem"
      padding={'.5rem 1rem'}
    >
      <Typography variant="caption">{children}</Typography>
      <Box
        flex="1"
        sx={{ backgroundColor: theme.vars.palette.ui3.main, height: '1px' }}
      ></Box>
    </Stack>
  );
};
